import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import {
  CustomInput,
  CustomSelect,
  CustomTextarea,
} from "components/partials/custom-form-inputs/custom-form-inputs";

import type { EmailAttributes } from "hooks/api/use-touchpoint-versions";
import { useBuilderContent } from "hooks/api/use-touchpoint-versions";

import { getSenderOnEmailTypeChange } from "containers/admin/clients/touchpoint/components/email-builder/email-builder-content-settings-tab/email-builder-content-settings-tab.helper";
import { CustomFormField, CustomSelectProps } from "utilities/forms";

import { IEmailType } from "containers/admin/clients/client/components/email-types/email-types.types";
import { ISenderDetails } from "containers/admin/clients/client/components/email-types/components/email-types-senders-form/email-types-senders-form.types";

import { DEFAULT_SENDER_DATA } from "containers/admin/clients/client/components/email-types/email-types.constants";

interface EmailBuilderContentSettingsTabProps {
  previewMode: boolean;
  formFields: CustomFormField[];
  emailTypeOptions: IEmailType[];
  currentTouchpointVersion: EmailAttributes;
  onSave: (field: Record<string, any>) => Promise<void>;
  setIsSaved: (isSaved: boolean) => void;
  setIsSaving: (isSaving: boolean) => void;
  setCurrentSender: React.Dispatch<React.SetStateAction<ISenderDetails>>;
  isTemplate: boolean;
}

const EmailBuilderContentSettingsTab = ({
  previewMode = false,
  formFields,
  emailTypeOptions,
  currentTouchpointVersion,
  onSave,
  setIsSaved,
  setIsSaving,
  setCurrentSender,
  isTemplate,
}: EmailBuilderContentSettingsTabProps) => {
  const initialAttributes = useBuilderContent() as EmailAttributes;

  const [hasChanges, setHasChanges] = useState(false);
  const lastSavedValuesRef = useRef<Record<string, unknown> | null>(null);

  const {
    control,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
    watch,
    trigger,
  } = useForm<Record<string, unknown>>({
    mode: "onChange",
  });

  useEffect(
    function setInitialValues() {
      formFields.forEach((field) => {
        const fieldName = field.name as keyof EmailAttributes;
        const initialValue = initialAttributes[fieldName] || field.defaultValue;
        setValue(fieldName, initialValue || "");
      });

      // Trigger validation to show errors for invalid initial values (e.g., blank required fields)
      trigger();

      // Create a snapshot of the initial values
      lastSavedValuesRef.current = getValues();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formFields],
  );

  useEffect(
    () => {
      //we find chosen emailType
      const emailType = emailTypeOptions.find(
        (option) => option.id === watch("iterableEmailTypeId"),
      )!;

      //we find chosen sender
      const sender = getSenderOnEmailTypeChange(emailType, currentTouchpointVersion);

      //set senderName to the form and trigger useEffect for senderName watch
      setValue("senderName", sender?.id);
    }, // eslint-disable-next-line
    [watch("iterableEmailTypeId")],
  );

  useEffect(
    () => {
      //we find chosen emailType
      const emailType = emailTypeOptions.find(
        (option) => option.id === watch("iterableEmailTypeId"),
      )!;

      //we find chosen sender among senders in chosen emailType
      const sender = emailType?.senders.length //check if we have senders
        ? emailType?.senders?.find((option: any) => option.id === watch("senderName"))!
        : DEFAULT_SENDER_DATA; //set a mock sender in case we dodn't have any senders on Email Type

      //set sender to the form
      setValue("senderEmail", sender?.senderEmail);
      setValue("replyToEmail", sender?.replyToEmail);

      //set sender outside for apply changes to Preview tab
      setCurrentSender(sender);
    }, // eslint-disable-next-line
    [watch("senderName")],
  );

  const watchValues = watch();

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const formData = getValues();
      const hasErrors = !!Object.keys(errors).length;

      await onSave({
        ...formData,
        publishable: isTemplate ? false : !hasErrors,
        disablePrefill: true,
        id: initialAttributes.id,
        name: initialAttributes.name,
        touchpointId: initialAttributes.touchpointId,
      });

      // Refresh saved values snapshot
      lastSavedValuesRef.current = formData;
      setIsSaved(true);
    } catch (error) {
      console.error("Failed to save", error);
      setIsSaved(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldBlur = async () => {
    if (hasChanges) {
      try {
        await handleSave();
      } catch (e) {
        console.error("Error when save onBlur", e);
      }
    }
  };

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(
    /** Autosave after 2 second debounce if a change has been made */
    function autosave() {
      if (hasChanges) {
        saveTimeoutRef.current = setTimeout(() => {
          handleSave();
        }, 2000);
      }

      return () => {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasChanges],
  );

  useEffect(() => {
    // Compare current values with snapshot to determine if there are changes
    const currentValues = getValues();
    const hasActualChanges =
      !isSubmitting &&
      lastSavedValuesRef.current !== null &&
      JSON.stringify(currentValues) !== JSON.stringify(lastSavedValuesRef.current);

    setHasChanges(hasActualChanges);
  }, [watchValues, isSubmitting, getValues]);

  const getDynamicOptions = (id: string) => {
    const chosenEmailType = emailTypeOptions.find((option) => option.id === id)!;

    return !chosenEmailType?.senders.length
      ? [
          {
            name: DEFAULT_SENDER_DATA.senderName,
            value: DEFAULT_SENDER_DATA.id,
          },
        ]
      : chosenEmailType?.senders.map(({ senderName, id }) => ({
          name: senderName,
          value: id,
        }));
  };

  return (
    <form
      className="flex flex-col max-w-[37.5rem] w-full mx-auto space-y-4"
      onSubmit={() => handleSave()}
      noValidate
    >
      {formFields.map((formField) => {
        if (formField.component === "hidden") {
          return null;
        }

        const isDynamicField = formField.name === "senderName";

        const senderOptions = getDynamicOptions(getValues("iterableEmailTypeId") as string);

        return (
          <div key={`${formField.name}-${formField.component}`}>
            <Controller
              control={control}
              name={formField.name}
              rules={{
                maxLength: formField.validations?.maxLength
                  ? {
                      value: formField.validations.maxLength,
                      message:
                        formField.validations.maxLengthValidationMessage ||
                        "Character limit exceeded",
                    }
                  : undefined,
                required: formField.validations?.isRequired
                  ? formField.validations.isRequiredValidationMessage || "This field is required"
                  : false,
              }}
              render={({ field: { onChange, onBlur, value } }) => {
                const commonProps = {
                  error: errors[formField.name],
                  eyebrow: formField.eyebrow,
                  helperText: formField.helperText,
                  label: formField.label,
                  name: formField.name,
                  onBlur: () => {
                    onBlur();
                    handleFieldBlur();
                  },
                  onChange,
                  placeholder: formField.placeholder,
                  required: !!formField.validations?.isRequired,
                  value: String(value ?? ""),
                  disabled: isDynamicField
                    ? senderOptions.length === 1
                    : previewMode || formField.disabled,
                  options: isDynamicField
                    ? senderOptions
                    : (formField as CustomSelectProps).options,
                };

                switch (formField.component) {
                  case "select":
                    return <CustomSelect {...commonProps} />;
                  case "textarea":
                    return <CustomTextarea {...commonProps} />;

                  default:
                    return (
                      <CustomInput
                        type="text"
                        addonStart={formField.addonStart}
                        addonEnd={formField.addonEnd}
                        {...commonProps}
                      />
                    );
                }
              }}
            />
          </div>
        );
      })}
    </form>
  );
};

export default EmailBuilderContentSettingsTab;
